import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────
interface SaftiPreview {
  imageUrl: string;
  propertyName: string;
  propertyPrice: string;
  propertyCity: string;
  facebookPost: string;
  hashtags: string[];
  propertyUrl: string;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting: { main_text: string; secondary_text: string };
}

interface PlaceDetails {
  nom_entreprise: string | null;
  activite: string | null;
  ville: string | null;
  site_web: string | null;
  formatted_address: string | null;
}

const OBJECTIVES_LIST = [
  'Plus de visibilité sur les réseaux sociaux',
  'Plus d\'appels entrants',
  'Plus de visites en magasin / cabinet',
  'Plus de demandes de devis',
  'Améliorer mon référencement Google local',
  'Fidéliser mes clients existants',
  'Promouvoir mes offres et promotions',
  'Partager mon expertise / conseils',
  'Recruter de nouveaux collaborateurs',
];

// ── OnboardingWizard ──────────────────────────────────────────────────────────
const OnboardingWizard: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1); // 1 = recherche établissement, 2 = profil, 3 = objectifs
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — Places search
  const [searchInput, setSearchInput] = useState('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [searching, setSearching] = useState(false);
  const [skippedSearch, setSkippedSearch] = useState(false); // eslint-disable-line
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2 — Profile
  const [nomEntreprise, setNomEntreprise] = useState(user?.nom_entreprise || '');
  const [activite, setActivite] = useState(user?.activite || '');
  const [ville, setVille] = useState(user?.ville || '');
  const [siteWeb, setSiteWeb] = useState(user?.site_web || '');
  const [description, setDescription] = useState(user?.description_activite || '');

  // Step 3 — Objectives
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>([]);
  const [customAngle, setCustomAngle] = useState('');

  // Step SAFTI preview
  const [showSaftiPreview, setShowSaftiPreview] = useState(false);
  const [saftiPreview, setSaftiPreview] = useState<SaftiPreview | null>(null);
  const [saftiLoading, setSaftiLoading] = useState(false);
  const [saftiError, setSaftiError] = useState<string | null>(null);

  const isSafti = siteWeb.toLowerCase().includes('safti.fr');

  // Le site est deja demande a l'inscription : on ne le redemande pas.
  // Derive du profil a chaque rendu, et recopie dans le champ des qu'il
  // arrive : le profil peut se charger APRES le premier rendu, et un
  // useState(user?.site_web) figeait alors une valeur vide (le parcours
  // « Remplir manuellement » reaffichait site et activite).
  const siteProfil = (user?.site_web || '').trim();
  const siteDejaConnu = !!siteProfil;
  useEffect(() => {
    if (siteProfil && !siteWeb.trim()) setSiteWeb(siteProfil);
    if (user?.ville && !ville.trim()) setVille(user.ville);
    if (user?.nom_entreprise && !nomEntreprise.trim()) setNomEntreprise(user.nom_entreprise);
  }, [siteProfil, user?.ville, user?.nom_entreprise]); // eslint-disable-line react-hooks/exhaustive-deps
  // Un conseiller d'un reseau connu est forcement agent immobilier : inutile
  // de lui faire taper son activite, on l'enregistre pour lui.
  const RESEAUX_IMMO = /safti\.fr|bskimmobilier|bsk-immobilier|reseau-hb|iadfrance|capifrance|optimhome|megagence|efficity|proprietes-privees|lafourmi-immo|expfrance|kw-france|kwfrance/i;
  const estReseauImmo = RESEAUX_IMMO.test(siteWeb);
  useEffect(() => {
    if (estReseauImmo && !activite.trim()) setActivite('Conseiller immobilier');
  }, [estReseauImmo]); // eslint-disable-line react-hooks/exhaustive-deps

  const token = localStorage.getItem('authToken');

  // ── Places autocomplete ──────────────────────────────────────────────────────
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchInput.trim().length < 2) { setPredictions([]); return; }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/places/autocomplete?input=${encodeURIComponent(searchInput)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setPredictions(data.predictions || []);
      } catch {
        setPredictions([]);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchInput]);

  const selectPlace = async (placeId: string) => {
    setPredictions([]);
    setSearchInput('');
    try {
      const res = await fetch(`/api/places/details?place_id=${placeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data: PlaceDetails = await res.json();
      if (data.nom_entreprise) setNomEntreprise(data.nom_entreprise);
      if (data.activite) setActivite(data.activite);
      if (data.ville) setVille(data.ville);
      // Conserver l'URL SAFTI originale si l'utilisateur est agent SAFTI
      const originalSiteWeb = user?.site_web || '';
      if (originalSiteWeb.toLowerCase().includes('safti.fr')) {
        setSiteWeb(originalSiteWeb);
      } else if (data.site_web) {
        setSiteWeb(data.site_web);
      }
      setStep(2);
    } catch {
      setError('Impossible de récupérer les détails. Remplissez le formulaire manuellement.');
      setStep(2);
    }
  };

  // ── Fetch SAFTI preview ───────────────────────────────────────────────────────
  const fetchSaftiPreview = async () => {
    setSaftiLoading(true);
    setSaftiError(null);
    setShowSaftiPreview(true);
    try {
      const res = await fetch('/api/ai/onboarding-preview', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteWeb }),
      });
      // Le serveur peut renvoyer une page HTML (route absente, proxy en
      // erreur) : on ne montre jamais au conseiller un message technique.
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.facebookPost) {
        throw new Error(data?.error || "L'aperçu n'est pas disponible pour le moment.");
      }
      setSaftiPreview({ ...data, hashtags: Array.isArray(data.hashtags) ? data.hashtags : [] });
    } catch (err: any) {
      setSaftiError(err.message);
    } finally {
      setSaftiLoading(false);
    }
  };

  // ── Toggle objective ──────────────────────────────────────────────────────────
  const toggleObjective = (obj: string) => {
    setSelectedObjectives(prev =>
      prev.includes(obj) ? prev.filter(o => o !== obj) : [...prev, obj]
    );
  };

  // ── Sauvegarde profil → passe à l'étape 4 (offre Stripe) ─────────────────────
  const saveProfile = async () => {
    setSaving(true);
    setError(null);
    try {
      const custom_angles = customAngle.trim() ? [customAngle.trim()] : [];
      const res = await fetch('/api/auth/onboarding', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom_entreprise: nomEntreprise,
          activite,
          ville,
          site_web: siteWeb,
          description_activite: description,
          objectives: selectedObjectives,
          custom_angles,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erreur');
      // On met à jour le profil mais on garde le wizard ouvert (étape 4 = Stripe)
      updateUser({ ...data.user, onboarding_completed: false });
      setStep(4);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Lancer l'essai gratuit via Stripe ─────────────────────────────────────────
  const handleStartTrial = async () => {
    setSubscribing(true);
    try {
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erreur');
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message);
      setSubscribing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        {step < 4 ? (
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-8 pt-8 pb-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-2xl">
                {showSaftiPreview ? '🏠' : step === 1 ? '🔍' : step === 2 ? '🏢' : '🎯'}
              </div>
              <div>
                <p className="text-white/70 text-xs uppercase tracking-widest font-medium">
                  {showSaftiPreview ? 'Aperçu SAFTI' : `Étape ${step} / 3`}
                </p>
                <h2 className="text-xl font-bold leading-tight">
                  {step === 1 && 'Trouvez votre établissement'}
                  {step === 2 && !showSaftiPreview && 'Votre profil entreprise'}
                  {showSaftiPreview && 'Votre dernier bien, prêt à publier'}
                  {step === 3 && !showSaftiPreview && 'Vos objectifs'}
                </h2>
              </div>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: showSaftiPreview ? '70%' : `${(step / 3) * 100}%` }} />
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-8 pt-8 pb-6 text-white text-center">
            <p className="text-white/60 text-xs uppercase tracking-widest font-medium mb-2">Votre profil est prêt</p>
            <h2 className="text-2xl font-bold">Boostez votre présence sur Facebook, Instagram, Google&nbsp;&amp;&nbsp;LinkedIn</h2>
            <p className="text-white/75 text-sm mt-1">Testez gratuitement pendant 30 jours, sans engagement.</p>
          </div>
        )}

        {/* Body */}
        <div className="px-8 py-6">

          {/* ── Step 1 — Search ── */}
          {step === 1 && (
            <div>
              <p className="text-sm text-gray-500 mb-5">
                Recherchez votre établissement sur Google pour pré-remplir votre profil automatiquement.
              </p>

              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Ex : Boulangerie Martin, Paris..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 pr-10"
                  autoFocus
                />
                {searching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  </span>
                )}

                {predictions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden">
                    {predictions.map(p => (
                      <li key={p.place_id}>
                        <button
                          onClick={() => selectPlace(p.place_id)}
                          className="w-full text-left px-4 py-3 hover:bg-violet-50 transition-colors border-b border-gray-50 last:border-0"
                        >
                          <p className="text-sm font-medium text-gray-800">{p.structured_formatting.main_text}</p>
                          <p className="text-xs text-gray-400">{p.structured_formatting.secondary_text}</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-100" />
                <span className="text-xs text-gray-400">ou</span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>

              <button
                onClick={() => { setSkippedSearch(true); setStep(2); }}
                className="mt-4 w-full py-2.5 rounded-xl text-sm text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Remplir manuellement
              </button>
            </div>
          )}

          {/* ── Step 2 — Profile ── */}
          {step === 2 && !showSaftiPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={nomEntreprise}
                    onChange={e => setNomEntreprise(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Ma Boulangerie"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Ville</label>
                  <input
                    type="text"
                    value={ville}
                    onChange={e => setVille(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Paris"
                  />
                </div>
              </div>

              {!estReseauImmo && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Activité / Secteur</label>
                <input
                  type="text"
                  value={activite}
                  onChange={e => setActivite(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Boulangerie, Plombier, Agence marketing..."
                />
              </div>
              )}

              {!siteDejaConnu && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Site web <span className="text-gray-400">(optionnel)</span></label>
                <input
                  type="url"
                  value={siteWeb}
                  onChange={e => setSiteWeb(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="https://monsite.fr"
                />
              </div>

              )}
            </div>
          )}

          {/* ── Step SAFTI Preview ── */}
          {showSaftiPreview && (
            <div>
              {saftiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <svg className="animate-spin w-10 h-10 text-violet-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  <p className="text-sm text-gray-500">On prépare votre premier post...</p>
                </div>
              )}

              {saftiError && (
                <div className="py-6 text-center">
                  <p className="text-sm text-red-500 mb-4">{saftiError}</p>
                  <button onClick={() => { setShowSaftiPreview(false); setStep(3); }}
                    className="text-sm text-violet-600 underline">Continuer sans aperçu</button>
                </div>
              )}

              {saftiPreview && !saftiLoading && (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video">
                    <img src={saftiPreview.imageUrl} alt={saftiPreview.propertyName}
                      className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12.07h2.54V9.845c0-2.522 1.492-3.915 3.777-3.915 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12.07h2.773l-.443 2.89h-2.33v6.988C20.343 21.201 24 17.064 24 12.073z"/></svg>
                      Aperçu Facebook
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-600">
                        {user?.username?.[0] || 'A'}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{user?.username}</p>
                        <p className="text-xs text-gray-400">À l'instant · 🌐</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {saftiPreview.facebookPost}
                    </p>
                    {saftiPreview.hashtags.length > 0 && (
                      <p className="mt-2 text-sm text-blue-600">
                        {saftiPreview.hashtags.join(' ')}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    Ce post sera automatiquement créé à chaque nouveau bien sur votre profil SAFTI.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3 — Objectives ── */}
          {step === 3 && !showSaftiPreview && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Sélectionnez vos objectifs principaux (plusieurs choix possibles).</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {OBJECTIVES_LIST.map(obj => (
                  <button
                    key={obj}
                    onClick={() => toggleObjective(obj)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      selectedObjectives.includes(obj)
                        ? 'bg-violet-600 text-white border-violet-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                    }`}
                  >
                    {obj}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Angle personnalisé <span className="text-gray-400">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={customAngle}
                  onChange={e => setCustomAngle(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Ex : Mettre en avant nos produits bio et locaux"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="mt-3 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}

          {/* ── Step 4 — Offre Stripe ── */}
          {step === 4 && (
            <div className="py-2">
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon: '📲', title: 'Facebook & Instagram', desc: 'Posts réguliers, stories et visuels pros' },
                  { icon: '📍', title: 'Google My Business', desc: 'Remontez en tête des recherches locales' },
                  { icon: '💼', title: 'LinkedIn', desc: 'Valorisez votre expertise auprès des pros' },
                  { icon: '⭐', title: 'Avis Google', desc: 'Collectez des avis, boostez votre note' },
                ].map(({ icon, title, desc }) => (
                  <div key={title} className="bg-gray-50 rounded-xl p-3">
                    <span className="text-2xl">{icon}</span>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 mb-5">
                <div>
                  <p className="text-violet-900 font-bold text-sm">30 jours offerts</p>
                  <p className="text-violet-600 text-xs mt-0.5">Puis 99 €/mois — annulable à tout moment</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-violet-500">Aucun prélèvement</p>
                  <p className="text-xs text-violet-500">pendant l'essai</p>
                </div>
              </div>

              <button
                onClick={handleStartTrial}
                disabled={subscribing}
                className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 transition-all shadow-md flex items-center justify-center gap-2 text-sm"
              >
                {subscribing ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Redirection...
                  </>
                ) : (
                  'Démarrer mon mois gratuit →'
                )}
              </button>

              <p className="mt-3 text-center text-xs text-gray-400">
                Aucun prélèvement pendant les 30 jours d'essai. Annulable à tout moment.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step < 4 && (
        <div className="px-8 pb-8 flex items-center justify-between">
          {step > 1 && !showSaftiPreview ? (
            <button onClick={() => setStep(s => s - 1)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              ← Retour
            </button>
          ) : showSaftiPreview ? (
            <button onClick={() => setShowSaftiPreview(false)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              ← Retour
            </button>
          ) : (
            <div />
          )}

          {/* Étape SAFTI preview */}
          {showSaftiPreview && !saftiLoading && (
            <button
              onClick={() => { setShowSaftiPreview(false); setStep(3); }}
              className="px-6 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-xl hover:bg-violet-700 transition-colors"
            >
              Continuer →
            </button>
          )}

          {/* Étapes normales */}
          {!showSaftiPreview && step < 3 && (
            <button
              onClick={() => {
                // Plus d'apercu SAFTI pendant l'inscription : il ralentissait
                // l'etape juste avant la carte et pouvait echouer. Le premier
                // post est genere apres, dans l'app.
                setStep(s => s + 1);
              }}
              disabled={step === 2 && !nomEntreprise.trim() && !activite.trim()}
              className="px-6 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continuer →
            </button>
          )}

          {!showSaftiPreview && step === 3 && (
            <button
              onClick={saveProfile}
              disabled={saving}
              className="px-6 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                'Finaliser mon profil →'
              )}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
